"use client";

import React from "react";
import { Download, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const clauses = [{
  id: "01.1",
  title: "Platform Purpose & Limitations",
  content: "The intelligence and analytics provided by this platform are designed for institutional reference and do not constitute binding financial, legal, or tax advice. Users must conduct independent due diligence before executing any market strategies.",
  isWarning: false
}, {
  id: "01.2",
  title: "INVESTMENT RISK",
  content: "All investments in infrastructure assets, including data center development and operation, carry inherent risk, including the risk of partial or total loss of invested capital. Projected returns, financial metrics, and operating assumptions presented on this website are illustrative and based on current market conditions, which are subject to change. Past performance of the principals or affiliated entities is not indicative of future results.",
  isWarning: true
}, {
  id: "02.1",
  title: "MARKET & INFORMATION RISK",
  content: "Market data, industry statistics, growth projections, and third-party research referenced on this website are sourced from publicly available reports and are believed to be reliable but are not independently verified by N2N Capital. N2N Capital makes no representation as to the accuracy, completeness, or timeliness of such information.",
  isWarning: false
}, {
  id: "03.1",
  title: "Regulatory Jurisdictions",
  content: "Access to certain network nodes and capital pools may be restricted based on your operational jurisdiction. It is the entity's strict responsibility to maintain compliance with local securities and exchange commission mandates.",
  isWarning: true
}, {
  id: "04.1",
  title: "DEVELOPMENT & EXECUTION RISK",
  content: "Infrastructure development projects are subject to construction delays, permitting challenges, power availability constraints, regulatory changes, cost overruns, and other execution risks. Pre-development assets and pipeline opportunities described on this website may not proceed as planned, and there is no guarantee that any specific project will be completed, achieve projected capacity, or generate expected returns.",
  isWarning: false
}];
function AnimatedClause({
  clause
}: {
  clause: typeof clauses[0];
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  if (clause.isWarning) {
    return <div ref={ref} className={cn("relative p-6 md:p-8 my-8 border border-primary rounded-[2px] transition-all duration-[600ms] ease-out bg-background", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        {}
        <div className="absolute inset-0 shadow-[0_0_15px_var(--color-primary)] opacity-[0.03] pointer-events-none rounded-[2px]" />
        
        <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8 relative z-10">
          <span className="font-mono text-primary font-medium tracking-wider shrink-0 md:w-20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {clause.id}
          </span>
          <div>
            <h3 className="text-xl font-semibold tracking-tight mb-3 text-foreground uppercase">
              {clause.title}
            </h3>
            <p className="text-foreground/90 leading-relaxed font-medium">
              {clause.content}
            </p>
          </div>
        </div>
      </div>;
  }
  return <div ref={ref} className={cn("py-8 flex flex-col md:flex-row md:items-start gap-4 md:gap-8 transition-all duration-[600ms] ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
      <span className="font-mono text-muted-foreground font-medium tracking-wider shrink-0 md:w-20">
        {clause.id}
      </span>
      <div>
        <h3 className="text-xl font-semibold tracking-tight mb-3 text-foreground uppercase">
          {clause.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed">
          {clause.content}
        </p>
      </div>
    </div>;
}
export default function Disclaimer() {
  return <section id="disclaimer" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
                Legal Disclosures & Risk Warnings
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Please review these critical operational and regulatory disclosures regarding the N2N Capital infrastructure platform.
              </p>
            </div>
            
          </div>

          <Separator className="mb-8" />

          <div className="divide-y divide-border">
            {clauses.map((clause, index) => <div key={clause.id} data-index={index}>
                <AnimatedClause clause={clause} />
              </div>)}
          </div>

          <div className="mt-16 p-8 rounded-lg bg-accent/50 border border-border">
            <p className="text-sm text-muted-foreground leading-relaxed text-center">Last Updated: April 2026. N2N Capital reserves the right to amend these disclosures at any time to reflect changes in regulatory requirements or platform architecture. Continued use of the platform constitutes acknowledgment of these terms.</p>
          </div>
        </div>
      </div>
    </section>;
}